import React, { useCallback, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { FormGroup } from '../../../../../../../components/form/FormGroup';
import InputLabel from '../../../../../../../components/form/InputLabel';
import Button from '../../../../../../../components/button';
import { useTranslation } from 'react-i18next';
import { useToasts } from '../../../../../../../context/Toast';
import { useMutationErrorHandler } from '../../../../../../../hooks/useMutationErrorHandler';
import { SelectSingleGoal } from '../SelectSingleGoal';
import { GoalId } from '../../types';
import { GoalRelatedGoal } from '../../types';
import { useGoalsContext } from '../../GoalsContext/GoalsContext';
import { useAvailableGoalsQuery } from '../../../../../__generated__/graphql';
import ServerError from '../../../../../../../components/ServerError';
import { mapGoalOption } from '../../useGoalSelectOptions';
import SingleFilter from '../../../../../../../components/SelectFilters/SingleFilter';
import { useAvailableGoalsFilter } from '../../useAvailableGoalsFilter';
import '../../GoalsModal.css';

type Props = {
  visible: boolean;
  onClose: () => void;
  sourceGoalId: GoalId;
  alignedTo: GoalRelatedGoal[];
  isProcessing: boolean;
  onAdd: (targetGoalId: GoalRelatedGoal) => Promise<void>;
};

const i18prefix = 'performanceReview.goals2.goalAlignedTo';
const filterI18prefix = 'performanceReview.goals2.pullGoal';

export const AddAlignedGoalModal: React.FC<Props> = ({
  visible,
  onClose,
  sourceGoalId,
  isProcessing,
  alignedTo,
  onAdd,
}) => {
  const { t } = useTranslation();
  const { addSuccess } = useToasts();
  const errorHandler = useMutationErrorHandler();
  const { currentPeriodId } = useGoalsContext();
  const [selectedGoalId, setSelectedGoalId] = React.useState<GoalId | null>(
    null,
  );

  const { data, error, loading } = useAvailableGoalsQuery({
    variables: {
      periodId: currentPeriodId,
    },
    skip: !visible,
  });

  const availableGoals = useMemo(() => {
    if (!data?.allObjectives) {
      return [];
    }
    return data.allObjectives.map(mapGoalOption);
  }, [data?.allObjectives]);

  const goalsExcludingSourceAndAligned = useMemo(
    () =>
      availableGoals.filter(
        (g) =>
          g.value !== sourceGoalId.id &&
          !alignedTo.some((a) => a.id === g.value),
      ),
    [availableGoals, sourceGoalId.id, alignedTo],
  );

  const {
    filterMode,
    filterModeOptions,
    filteredGoals,
    handleFilterModeChange,
    resetFilterMode,
  } = useAvailableGoalsFilter({
    allGoals: goalsExcludingSourceAndAligned,
    setSelectedGoalId,
  });

  const goalsToShow = filteredGoals;

  const handleClose = useCallback(() => {
    setSelectedGoalId(null);
    resetFilterMode();
    onClose();
  }, [onClose, resetFilterMode]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedGoalId) return;
      const foundGoal = goalsToShow.find((g) => g.value === selectedGoalId.id);
      const targetGoal: GoalRelatedGoal = {
        id: selectedGoalId.id,
        type: selectedGoalId.type,
        name: foundGoal?.label ?? '',
        employee: foundGoal?.goalEmployee,
        team: foundGoal?.goalTeam,
      };
      await onAdd(targetGoal)
        .then(() => {
          addSuccess(t(`${i18prefix}.addSuccess`));
          handleClose();
        })
        .catch(errorHandler);
    },
    [
      selectedGoalId,
      onAdd,
      handleClose,
      addSuccess,
      t,
      errorHandler,
      goalsToShow,
    ],
  );

  if (!visible) {
    return null;
  }

  return (
    <Modal
      backdropClassName="goals-modal-backdrop"
      contentClassName="goals-modal-content border"
      show={visible}
      onHide={handleClose}
      size="lg"
      centered>
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title>{t(`${i18prefix}.addTitle`)}</Modal.Title>
            <div>{t(`${i18prefix}.addSubtitle`)}</div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-4">
          {error ? (
            <ServerError />
          ) : (
            <>
              <FormGroup>
                <InputLabel label={t(`${filterI18prefix}.filterLabel`)} />

                <SingleFilter
                  placeholder={t(`${filterI18prefix}.filterPlaceholder`)}
                  options={filterModeOptions}
                  value={filterMode}
                  onChange={handleFilterModeChange}
                  isClearable={false}
                />
              </FormGroup>
              <FormGroup>
                <InputLabel label={t(`${i18prefix}.selectGoalLabel`)} />
                <SelectSingleGoal
                  value={selectedGoalId}
                  options={goalsToShow}
                  placeholder={t(`${i18prefix}.selectGoalPlaceholder`)}
                  isDisabled={loading}
                  onChange={setSelectedGoalId}
                />
              </FormGroup>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            label={t(`${i18prefix}.cancel`)}
            onClick={handleClose}
            disabled={isProcessing}
          />
          {!error && (
            <Button
              variant="primary"
              label={t(`${i18prefix}.alignToGoal`)}
              type="submit"
              isProcessing={isProcessing}
              disabled={!selectedGoalId || loading || isProcessing}
            />
          )}
        </Modal.Footer>
      </form>
    </Modal>
  );
};
